import { useState } from 'react'
import { ConeTecMark } from './icons'
import { TranslatedLine, useOriginal } from './TranslatedLine'
import type { SupportNote } from '../lib/supportStaff'
import { PersonField } from './GiveRecognition'
import type { PersonOption } from '../lib/stars'
import { laneLabel, type SupportLane } from '../lib/support'
import { SupportStarAward } from './SupportStarAward'
import { GuideLink } from './guides/GuideLink'
import type { SupportStarLevel, SupportStars } from '../lib/supportStars'
import {
  CATEGORY_WORDS,
  handoffState,
  wikiAnswerState,
  type SupportStaffView,
} from '../lib/supportStaff'

/**
 * The lane holder's side panel on `#/support/:id` — 117, HLP-27.
 *
 * ENGLISH ON PURPOSE, like the inbox and the admin screens: the people holding
 * a lane are staff (SHL-25 keeps those screens English), so none of this is in
 * hubCopy.ts and the "one home" guard does not list this file.
 *
 * In components/ and props-only for the suite's sake (see SupportThread.tsx).
 *
 * ── THREE SECTIONS, THREE AUTHORS ───────────────────────────────────────────
 *
 *   The triage is a MACHINE's — teal plus the mark, the machine-authorship
 *   disclosure — because a staff member reading "suspected cause: …" must be
 *   able to tell a guess from a colleague's view before acting on it.
 *   The notes are people's, by name.
 *   The handoff is the lane holder's own approval, and it is the ONLY thing
 *   that leaves the platform.
 *
 * ── THE REWRITE IS A HUMAN ACT (HLP-7) ──────────────────────────────────────
 *
 * The form is pre-filled from the machine's draft, and says in plain words
 * what must not be in it before anybody presses Send. Removing the name is not
 * enough — a start year, a brand and a nine-person BU identify a person — so
 * the sentence names all three kinds of thing.
 */
export function SupportStaffPanel({
  view,
  onSend,
  busy = false,
  error,
  handed,
  onHand,
  canHandOn = false,
  onWikiDraft,
  onWikiConfirm,
  moveTargets = [],
  onMove,
  stars,
  onAward,
  onDismiss,
}: {
  view: SupportStaffView
  /** 117. Wired only for a HOLDER of the lane — the server refuses the
   *  assignee too, so the door is not drawn for them (rule 4). */
  onSend?: (title: string, body: string) => void
  busy?: boolean
  error?: string | null
  /** 119. Who the question is handed to, from the thread head. */
  handed?: { to: string | null; at: string | null; iAmAssignee: boolean }
  /** 119. Hand to a colleague (personId + reason), or back (null). Wired for
   *  a holder (hand and take back) and for the assignee (hand back only);
   *  `canHandOn` says which, so the form is drawn only where it works. */
  onHand?: (personId: string | null, note?: string) => void
  canHandOn?: boolean
  /** 120. Ask the machine for a Wiki One draft (lane or assignee), and
   *  confirm it as a draft answer in your name (HOLDER only — the server
   *  refuses the assignee; the door is not drawn for them). */
  onWikiDraft?: () => void
  onWikiConfirm?: (question: string, answer: string) => void
  /** 121. Where this question may be moved — the open lanes minus this one
   *  minus the held HR lane, from `moveTargets()` in lib/support. Empty means
   *  no door is drawn (rule 4). Wired for a HOLDER only: the server refuses
   *  the assignee, so the door is not drawn for them. */
  moveTargets?: SupportLane[]
  onMove?: (lane: SupportLane, reason: string) => void
  /** 172. Stars on a platform ticket; null/undefined draws nothing. `onAward`
   *  is wired for a HOLDER; the server's canAward decides the buttons. */
  stars?: SupportStars | null
  onAward?: (level: SupportStarLevel) => void
  /** 174. The panel is the desk's Details TRAY: this closes it. Absent on a
   *  screen where the panel is not a tray. */
  onDismiss?: () => void
}) {
  const { triage, handoff, notes, lane } = view
  const filed = !!handoff?.trackerIssueId
  const [pick, setPick] = useState<PersonOption | null>(null)
  const [why, setWhy] = useState('')
  const [handing, setHanding] = useState(false)
  // 121. The move form. `moveTo` starts on the first target so a one-lane
  // list (the usual case while HR is held) needs no choosing.
  const [moving, setMoving] = useState(false)
  const [moveTo, setMoveTo] = useState<SupportLane | ''>(moveTargets[0] ?? '')
  const [moveWhy, setMoveWhy] = useState('')
  // 120. The holder's edit of the machine's draft. Keyed by the parent on the
  // draft's time so a re-draft resets these to the new suggestion.
  const [wq, setWq] = useState(view.wikiDraft?.question ?? '')
  const [wa, setWa] = useState(view.wikiDraft?.answer ?? '')
  const [title, setTitle] = useState(handoff?.title ?? triage?.devTitleDraft ?? '')
  const [body, setBody] = useState(handoff?.body ?? triage?.devBodyDraft ?? '')
  const [composing, setComposing] = useState(false)
  /* 174. ONE TASK AT A TIME. Sending to developers, handing on and moving
     lanes each open a form with a warning that has to be READ (who will see
     what). In the tray the form takes the whole panel, with a way back,
     instead of opening in the middle of six other sections. */
  const task: 'dev' | 'hand' | 'move' | null = composing ? 'dev' : handing ? 'hand' : moving ? 'move' : null
  const backToDetails = () => {
    setComposing(false)
    setHanding(false)
    setPick(null)
    setWhy('')
    setMoving(false)
    setMoveWhy('')
  }

  return (
    <section className="card supportstaff" id="support-details" aria-label="For the support team">
      <header className="supportstaff__top">
        {task ? (
          <button type="button" className="btn btn--quiet btn--sm supportstaff__back" onClick={backToDetails} disabled={busy}>
            ← Details
          </button>
        ) : (
          <h3 className="supportstaff__head">Details</h3>
        )}
        {onDismiss && (
          <button type="button" className="btn btn--quiet btn--sm supportstaff__dismiss" onClick={onDismiss} aria-label="Close details">
            ✕
          </button>
        )}
      </header>
      {!task && (
        <p className="supportstaff__lede">For the support team. Only people who answer this lane see this panel. The person who asked does not.</p>
      )}

      {/* ── the machine's reading ─────────────────────────────────────── */}
      {task ? null : triage ? (
        <div className="supportstaff__triage">
          <p className="supportstaff__by">
            <span className="supportstaff__mark" aria-hidden="true"><ConeTecMark size={14} /></span>
            Triaged by Claude · {new Date(triage.triagedAt).toLocaleDateString('en-CA')} · a suggestion, not a decision
          </p>
          <p className="supportstaff__tags">
            <span className="pill">{CATEGORY_WORDS[triage.category]}</span>
            <span className={`pill${triage.severity === 'high' ? ' supportstaff__sev--high' : ''}`}>
              {triage.severity === 'high' ? 'High severity' : triage.severity === 'low' ? 'Low severity' : 'Normal severity'}
            </span>
          </p>
          <p className="supportstaff__summary">{triage.summary}</p>
          {/* FOLDED (29 Sep). The cause and the fix run to a dozen lines of
              file paths each — useful to whoever writes the fix, and a wall
              between the summary and every action below it for everyone else. */}
          {(triage.suspectedCause || triage.recommendation) && (
            <details className="supportstaff__why">
              <summary>Suspected cause and recommended fix</summary>
              {triage.suspectedCause && (
                <p className="supportstaff__field"><strong>Suspected cause.</strong> {triage.suspectedCause}</p>
              )}
              {triage.recommendation && (
                <p className="supportstaff__field"><strong>Recommended fix.</strong> {triage.recommendation}</p>
              )}
            </details>
          )}
        </div>
      ) : (
        <p className="supportstaff__empty">
          {lane === 'platform' ? 'Not triaged yet. The daily run reads new platform questions each morning.' : 'Only platform questions are triaged.'}
        </p>
      )}

      {/* ── developers ───────────────────────────────────────────────────
          SECOND, DIRECTLY UNDER THE MACHINE'S READING (29 Sep). It was the
          last block in a panel taller than the screen, and David replied on
          his own ticket "i don't see send to dev" — the button existed, eight
          sections down. The triage recommends; this is where it gets acted on,
          so the two sit together. */}
      {lane === 'platform' && (!task || task === 'dev') && (
        <>
          <h4 className="supportstaff__sub">Developers</h4>
          <GuideLink guide="support-desk" section="developers" label="How sending to developers works" />
          <p className="supportstaff__state" aria-live="polite">{handoffState(handoff)}</p>
          {!filed && onSend && !composing && (
            <button type="button" className="btn btn--sm" onClick={() => setComposing(true)} disabled={busy}>
              {handoff ? 'Edit what developers will see' : 'Send to developers'}
            </button>
          )}
          {!filed && onSend && composing && (
            <form
              className="supportstaff__form"
              onSubmit={(e) => {
                e.preventDefault()
                if (!title.trim() || !body.trim()) return
                onSend(title.trim(), body.trim())
                setComposing(false)
              }}
            >
              <p className="supportstaff__warn">
                Developers see only this. Write it so nobody could tell who asked — no name, no business unit or site,
                none of their exact words. Their screenshots and page details stay here.
              </p>
              <label className="supportstaff__label" htmlFor="dev-title">Title</label>
              <input
                id="dev-title"
                className="composer__body supportstaff__title"
                maxLength={200}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={busy}
              />
              <label className="supportstaff__label" htmlFor="dev-body">What developers need to know</label>
              <textarea
                id="dev-body"
                className="composer__body"
                rows={6}
                maxLength={8000}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                disabled={busy}
              />
              <div className="supportstaff__actions">
                <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !title.trim() || !body.trim()}>
                  Approve for developers
                </button>
                <button type="button" className="btn btn--quiet btn--sm" onClick={() => setComposing(false)} disabled={busy}>
                  Cancel
                </button>
              </div>
            </form>
          )}
          {handoff && (
            <details className="supportq__one">
              <summary>What developers {filed ? 'were given' : 'will be given'}</summary>
              <p className="supportstaff__field"><strong>{handoff.title}</strong></p>
              <p className="supportstaff__notebody">{handoff.body}</p>
              <p className="supportstaff__notewho">
                Approved by {handoff.requestedBy ?? 'somebody'} · {new Date(handoff.requestedAt).toLocaleDateString('en-CA')}
              </p>
            </details>
          )}
        </>
      )}

      {/* ── 172: stars, right under Developers — the same reading of the
          ticket ("is this a real bug, how bad") decides both. */}
      {!task && lane === 'platform' && stars && (
        <>
          <SupportStarAward stars={stars} onAward={onAward} busy={busy} />
          <GuideLink guide="support-desk" section="stars" label="How stars for a ticket work" />
        </>
      )}

      {/* ── colleague notes ───────────────────────────────────────────────
          THE LIST ONLY. The box that writes one moved into the thread's
          composer as its "Note for the team" mode (118) — two note boxes on
          one screen is the "two of anything" this app keeps refusing, and the
          composer is where the holder's hands already are. */}
      {!task && <h4 className="supportstaff__sub">Internal notes</h4>}
      {task ? null : notes.length === 0 ? (
        <p className="supportstaff__empty">No notes yet. Notes are written under the conversation.</p>
      ) : (
        <ul className="supportstaff__notes">
          {notes.map((n) => (
            <li key={n.id}>
              <p className="supportstaff__notewho">
                {n.author ?? 'Somebody'} · {new Date(n.createdAt).toLocaleDateString('en-CA')}
              </p>
              <NoteBody note={n} />
            </li>
          ))}
        </ul>
      )}

      {/* ── handed to a colleague (119) ───────────────────────────────────
          A COLUMN ON THE QUESTION, NOT A ROLE. One named person — who may hold
          no lane — reads THIS thread and nothing else. The reason is required
          and becomes a note above; the asker is told who has it. */}
      {(onHand || handed?.to) && (!task || task === 'hand') && (
        <>
          <h4 className="supportstaff__sub">Handed to a colleague</h4>
          <GuideLink guide="support-desk" section="hand" label="How handing a thread works" />
          {handed?.to ? (
            <>
              <p className="supportstaff__state">
                {handed.iAmAssignee ? 'Handed to you' : `Handed to ${handed.to}`}
                {handed.at ? ` · ${new Date(handed.at).toLocaleDateString('en-CA')}` : ''}.
                {' '}They can read and reply to this thread, and nothing else in the lane.
              </p>
              {onHand && (
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={busy}
                  onClick={() => onHand(null)}
                >
                  {handed.iAmAssignee ? 'Hand it back' : 'Take it back'}
                </button>
              )}
            </>
          ) : onHand && canHandOn ? (
            !handing ? (
              <button type="button" className="btn btn--sm" disabled={busy} onClick={() => setHanding(true)}>
                Hand to a colleague
              </button>
            ) : (
              <form
                className="supportstaff__form"
                onSubmit={(e) => {
                  e.preventDefault()
                  if (!pick || !why.trim()) return
                  onHand(pick.personId, why.trim())
                  setHanding(false)
                  setPick(null)
                  setWhy('')
                }}
              >
                <p className="supportstaff__warn">
                  They will see this whole thread — the question, the pictures, the replies and these notes — and
                  nothing else in the lane. The person who asked is told who has it.
                </p>
                <PersonField person={pick} onPick={setPick} label="Who" />
                <label className="supportstaff__label" htmlFor="hand-why">Why them</label>
                <textarea
                  id="hand-why"
                  className="composer__body"
                  rows={2}
                  maxLength={1000}
                  placeholder="Kept as a note for the team; the asker never sees it."
                  value={why}
                  onChange={(e) => setWhy(e.target.value)}
                  disabled={busy}
                />
                <div className="supportstaff__actions">
                  <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !pick || !why.trim()}>
                    Hand it to them
                  </button>
                  <button type="button" className="btn btn--quiet btn--sm" disabled={busy} onClick={() => { setHanding(false); setPick(null); setWhy('') }}>
                    Cancel
                  </button>
                </div>
              </form>
            )
          ) : null}
        </>
      )}

      {/* ── another lane (121, HLP-4) ─────────────────────────────────────
          THE ASKER PICKED THE LANE (089) AND ROUTING IS NEVER RIGHT EVERY TIME.
          A holder of THIS lane moves it, with a reason that becomes a note the
          new lane reads. The asker is told where it went. Nothing suggests a
          lane: person picks, system watches, suggestion last. */}
      {onMove && moveTargets.length > 0 && (!task || task === 'move') && (
        <>
          <h4 className="supportstaff__sub">Lane</h4>
          <GuideLink guide="support-desk" section="move" label="How moving a thread works" />
          <p className="supportstaff__state">In {laneLabel(lane)}.</p>
          {!moving ? (
            <button type="button" className="btn btn--sm" disabled={busy} onClick={() => setMoving(true)}>
              Move to another lane
            </button>
          ) : (
            <form
              className="supportstaff__form"
              onSubmit={(e) => {
                e.preventDefault()
                if (!moveTo || !moveWhy.trim()) return
                onMove(moveTo, moveWhy.trim())
                setMoving(false)
                setMoveWhy('')
              }}
            >
              <p className="supportstaff__warn">
                That lane's team will read this whole thread and these notes, and this lane will not see it again.
                The person who asked is told where it went.
              </p>
              <label className="supportstaff__label" htmlFor="move-lane">To</label>
              <select
                id="move-lane"
                className="supportstaff__select"
                value={moveTo}
                onChange={(e) => setMoveTo(e.target.value as SupportLane)}
                disabled={busy}
              >
                {moveTargets.map((l) => (
                  <option key={l} value={l}>{laneLabel(l)}</option>
                ))}
              </select>
              <label className="supportstaff__label" htmlFor="move-why">Why</label>
              <textarea
                id="move-why"
                className="composer__body"
                rows={2}
                maxLength={1000}
                placeholder="Kept as a note for the team; the asker never sees it."
                value={moveWhy}
                onChange={(e) => setMoveWhy(e.target.value)}
                disabled={busy}
              />
              <div className="supportstaff__actions">
                <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !moveTo || !moveWhy.trim()}>
                  Move it
                </button>
                <button type="button" className="btn btn--quiet btn--sm" disabled={busy} onClick={() => { setMoving(false); setMoveWhy('') }}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </>
      )}

      {/* ── Wiki One (120) ─────────────────────────────────────────────────
          THREE HANDS: the machine drafts (teal — its words, a suggestion), the
          holder confirms in their own name, a curator publishes. This panel
          never publishes: `publish_wiki_content` needs content.publish, which
          the support roles do not hold (085), and B18 says curation by a named
          human is the review. */}
      {!task && (onWikiDraft || onWikiConfirm || view.wikiAnswer) && (
        <>
          <h4 className="supportstaff__sub">Wiki One</h4>
          <GuideLink guide="support-desk" section="wiki" label="How a thread becomes a Wiki One answer" />
          <p className="supportstaff__state" aria-live="polite">{wikiAnswerState(view.wikiAnswer)}</p>
          {view.wikiAnswer && view.wikiAnswer.title && (
            <p className="supportstaff__field"><strong>{view.wikiAnswer.title}</strong></p>
          )}
          {!view.wikiAnswer && (
            <>
              {view.wikiDraft ? (
                <div className="supportstaff__triage">
                  <p className="supportstaff__by">
                    <span className="supportstaff__mark" aria-hidden="true"><ConeTecMark size={14} /></span>
                    Suggested by Claude · {new Date(view.wikiDraft.draftedAt).toLocaleDateString('en-CA')} · from the thread, with no names — a suggestion, not a decision
                  </p>
                  {view.wikiDraft.usable ? (
                    onWikiConfirm ? (
                      <form
                        className="supportstaff__form"
                        onSubmit={(e) => {
                          e.preventDefault()
                          if (!wq.trim() || !wa.trim()) return
                          onWikiConfirm(wq.trim(), wa.trim())
                        }}
                      >
                        <p className="supportstaff__warn">
                          This becomes a Wiki One entry in your name, for everyone. Check it says only what the lane
                          actually said, and that nothing in it points at who asked.
                        </p>
                        <label className="supportstaff__label" htmlFor="wiki-q">Question</label>
                        <input
                          id="wiki-q"
                          className="composer__body supportstaff__title"
                          maxLength={300}
                          value={wq}
                          onChange={(e) => setWq(e.target.value)}
                          disabled={busy}
                        />
                        <label className="supportstaff__label" htmlFor="wiki-a">Answer</label>
                        <textarea
                          id="wiki-a"
                          className="composer__body"
                          rows={6}
                          maxLength={6000}
                          value={wa}
                          onChange={(e) => setWa(e.target.value)}
                          disabled={busy}
                        />
                        <div className="supportstaff__actions">
                          <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !wq.trim() || !wa.trim()}>
                            Send to Wiki One as a draft
                          </button>
                          {onWikiDraft && (
                            <button type="button" className="btn btn--quiet btn--sm" disabled={busy} onClick={onWikiDraft}>
                              Suggest again
                            </button>
                          )}
                        </div>
                      </form>
                    ) : (
                      <>
                        <p className="supportstaff__field"><strong>{view.wikiDraft.question}</strong></p>
                        <p className="supportstaff__notebody">{view.wikiDraft.answer}</p>
                        <p className="supportstaff__empty">A holder of the lane sends it to Wiki One.</p>
                      </>
                    )
                  ) : (
                    <p className="supportstaff__field">{view.wikiDraft.reason ?? 'Nothing to add yet.'}</p>
                  )}
                </div>
              ) : (
                <p className="supportstaff__empty">
                  Once the lane has answered, the machine can suggest a Wiki One entry from this thread.
                </p>
              )}
              {onWikiDraft && !view.wikiDraft && (
                <button type="button" className="btn btn--sm" disabled={busy} onClick={onWikiDraft}>
                  Suggest a Wiki One entry
                </button>
              )}
              {onWikiDraft && view.wikiDraft && !view.wikiDraft.usable && (
                <button type="button" className="btn btn--sm" disabled={busy} onClick={onWikiDraft}>
                  Suggest again
                </button>
              )}
            </>
          )}
        </>
      )}

      {error && <p className="roles__error" role="alert">{error}</p>}
    </section>
  )
}

/**
 * 123. A note's body, in the reader's language when One rendered it, with
 * the same teal line and Show original as a reply — the original travels
 * with the note (support_staff_view carries it), so the loader resolves
 * without a call.
 */
function NoteBody({ note }: { note: SupportNote }) {
  const orig = useOriginal(note.translationStatus === 'machine', () =>
    Promise.resolve(note.originalBody === null ? null : { title: '', body: note.originalBody, locale: note.originalLocale }),
  )
  return (
    <>
      <TranslatedLine state={orig} />
      <p className="supportstaff__notebody">{orig.showingOriginal && orig.original ? orig.original.body : note.body}</p>
    </>
  )
}
